import CloseOutlined from '@ant-design/icons/CloseOutlined';
import FullscreenExitOutlined from '@ant-design/icons/FullscreenExitOutlined';
import FullscreenOutlined from '@ant-design/icons/FullscreenOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import Button from 'antd/es/button';
import Modal from 'antd/es/modal';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';

import { RunPipeline } from './RunPipeline';

interface RunModalProps {
  open: boolean;
  onClose: () => void;
  pipelineId: string;
  configVersionId: string;
  inputSchema: Record<string, unknown> | null;
  pipelineConfig: Record<string, unknown> | null;
  isValidating: boolean;
  teamId: string;
  onExecutionStatusChange: (isRunning: boolean) => void;
}

const ModalContainer = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
`;

const ModalContent = styled.div<{ $isFullscreen: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ $isFullscreen }) =>
    $isFullscreen ? 'calc(100vh - 55px)' : '60vh'};
  min-height: ${({ $isFullscreen }) =>
    $isFullscreen ? 'calc(100vh - 55px)' : '400px'};
  max-height: ${({ $isFullscreen }) =>
    $isFullscreen ? 'calc(100vh - 55px)' : '80vh'};
`;

const ModalTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.marginXS}px;
  flex: 1;
`;

const ModalHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.marginXS}px;
  margin-left: auto;
`;

const HeaderButton = styled(Button)`
  border: none;
  background: transparent;
  padding: ${({ theme }) => theme.paddingXXS}px;

  &:hover {
    background: ${({ theme }) => theme.colorFillContent};
  }
`;

export const RunModal: React.FC<RunModalProps> = ({
  open,
  onClose,
  pipelineId,
  configVersionId,
  inputSchema,
  pipelineConfig,
  isValidating,
  teamId,
  onExecutionStatusChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Fullscreen state from search params
  const isFullscreen = searchParams.get('fullscreen') === 'true';
  const setIsFullscreen = (fullscreen: boolean) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (fullscreen) {
        newParams.set('fullscreen', 'true');
      } else {
        newParams.delete('fullscreen');
      }
      return newParams;
    });
  };

  // Reset fullscreen state when modal closes
  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  return (
    <ModalContainer
      title={
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <ModalTitleContainer>
            <PlayCircleOutlined />
            Run Pipeline
          </ModalTitleContainer>
          <ModalHeaderActions>
            <HeaderButton
              type="text"
              icon={
                isFullscreen ? (
                  <FullscreenExitOutlined />
                ) : (
                  <FullscreenOutlined />
                )
              }
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            />
            <HeaderButton
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              title="Close modal"
            />
          </ModalHeaderActions>
        </div>
      }
      open={open}
      onCancel={handleClose}
      closable={false}
      footer={null}
      width={isFullscreen ? '100vw' : 1200}
      style={
        isFullscreen
          ? {
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 0,
              maxWidth: '100vw',
              height: '100vh',
            }
          : undefined
      }
      centered={!isFullscreen}
      destroyOnHidden
      mask={!isFullscreen}
      maskClosable={!isFullscreen}
    >
      <ModalContent $isFullscreen={isFullscreen}>
        <RunPipeline
          pipelineId={pipelineId}
          configVersionId={configVersionId}
          inputSchema={inputSchema}
          pipelineConfig={pipelineConfig}
          isValidating={isValidating}
          teamId={teamId}
          onExecutionStatusChange={onExecutionStatusChange}
        />
      </ModalContent>
    </ModalContainer>
  );
};
